import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { History, Plus, Eye, EyeOff, CalendarClock, CheckCircle2, ClipboardList, Link2, Trash2, Sparkles } from "lucide-react";
import CrownMaterialCompare from "./CrownMaterialCompare";

import {
  UPPER_RIGHT, UPPER_LEFT, LOWER_LEFT, LOWER_RIGHT,
  isAnterior, isCanine, isPremolar, isMolar, isUpper,
  POSTERIOR_SURFACES, ANTERIOR_SURFACES, STATUS_OPTS, BASE_STATUS_OPTS,
  ROOT_OPTS, CROWN_OPTS, deriveLayers,
  CATEGORY_COLOR, categorizeByName, SPRITE_ASPECT, spriteKeyFor, ROOT_CANALS,
  CROWN_MATERIALS, MATERIAL_LABEL, MAT_FX, CONNECTOR_FX,
} from "./toothUtils";

const VISUAL_UPPER = [...UPPER_RIGHT, ...UPPER_LEFT];
const VISUAL_LOWER = [...LOWER_RIGHT.slice().reverse(), ...LOWER_LEFT.slice().reverse()];
const visualIndex = (n) => { const u = VISUAL_UPPER.indexOf(n); return u >= 0 ? u : VISUAL_LOWER.indexOf(n); };

const Odontogram3D = lazy(() => import("./Odontogram3D"));

// Surface-region SVG paths within a 64x96 tooth
// We render two views per tooth: occlusal/incisal table (top) + facial silhouette (bottom)
const W = 64, H = 150, CX = W / 2;

// Surface regions inside the occlusal/incisal "table" — a curved area at top of tooth
const surfacePath = (surface, anterior) => {
  // Occlusal/incisal table region: rounded shape from ~y=12 to ~y=42
  const x0 = 14, y0 = 14, x1 = W - 14, y1 = 42;
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  // Inner center region for O/I
  if (surface === "O" || surface === "I") {
    return `M${cx - 9} ${cy - 7} Q${cx} ${cy - 9} ${cx + 9} ${cy - 7} L${cx + 9} ${cy + 7} Q${cx} ${cy + 9} ${cx - 9} ${cy + 7} Z`;
  }
  if (surface === "M") return `M${x0} ${y0} Q${cx} ${y0 - 1} ${x1} ${y0} L${cx + 9} ${cy - 7} Q${cx} ${cy - 9} ${cx - 9} ${cy - 7} Z`;
  if (surface === "D") return `M${x0} ${y1} Q${cx} ${y1 + 1} ${x1} ${y1} L${cx + 9} ${cy + 7} Q${cx} ${cy + 9} ${cx - 9} ${cy + 7} Z`;
  if (surface === "B") return `M${x0} ${y0} Q${x0 - 1} ${cy} ${x0} ${y1} L${cx - 9} ${cy + 7} L${cx - 9} ${cy - 7} Z`;
  if (surface === "L") return `M${x1} ${y0} Q${x1 + 1} ${cy} ${x1} ${y1} L${cx + 9} ${cy + 7} L${cx + 9} ${cy - 7} Z`;
  // Cervikale: brez në qafën e dhëmbit (nën tabelën okluzale, mbi kufirin kurorë-rrënjë)
  if (surface === "C") return `M18 45 Q${cx} 43 ${W - 18} 45 L${W - 18} 52 Q${cx} 54.5 18 52 Z`;
  return "";
};

// Kurora protetike: sprite-i real i dhëmbit me filtër materiali (duket si qeramikë e vërtetë)
const crownLayer = (material, sprite, number, drawnX, drawnY, drawnW, drawnH, cervY) => {
  if (!material) {
    return <ellipse cx={CX} cy={drawnY + drawnH * 0.21} rx={drawnW * 0.46} ry={drawnH * 0.21} fill="#C9963A" fillOpacity="0.18" stroke="#C9963A" strokeWidth="2.2" />;
  }
  return (
    <g>
      <image href={sprite} x={drawnX} y={drawnY} width={drawnW} height={drawnH} clipPath={`url(#crownclip-${number})`} filter={`url(#matfx-${number})`} />
      {material === "metal_porcelan" ? (
        <rect x={drawnX + drawnW * 0.1} y={cervY - 4} width={drawnW * 0.8} height={4.5} rx={2} fill="#7C8590" />
      ) : (
        <path d={`M ${drawnX + drawnW * 0.08} ${cervY - 1} Q ${CX} ${cervY + 1.5} ${drawnX + drawnW * 0.92} ${cervY - 1}`}
              stroke={material === "zirkon" ? "#AEB9C2" : "#C4B491"} strokeWidth="1.4" fill="none" opacity="0.9" />
      )}
      {material === "zirkon_emax" && (
        <path d={`M ${CX - drawnW * 0.22} ${drawnY + drawnH * 0.1} Q ${CX} ${drawnY + drawnH * 0.04} ${CX + drawnW * 0.22} ${drawnY + drawnH * 0.1}`}
              stroke="#FFFFFF" strokeWidth="2.2" fill="none" opacity="0.75" strokeLinecap="round" />
      )}
    </g>
  );
};

function Tooth({ number, record, treatments, plans, showPlans, onClick, bridgeInfo, bridgeSelected }) {
  const status = record?.status || "healthy";
  const anterior = isAnterior(number);
  const surfaces = anterior ? ANTERIOR_SURFACES : POSTERIOR_SURFACES;
  const upper = isUpper(number);

  // Aggregate surface colors from completed treatments
  const surfaceColors = {};
  // Track latest filling class per tooth (Open Dental shows the most recent restoration)
  let latestFilling = null;
  (treatments || []).forEach((t) => {
    const cat = t.category || categorizeByName(t.name);
    const color = CATEGORY_COLOR[cat] || CATEGORY_COLOR.default;
    (t.surfaces || []).forEach((s) => { surfaceColors[s] = color; });
    if (cat === "Mbushje" && (t.filling_class || /Klasa\s+(I{1,3}V?|V)/.test(t.name || ""))) {
      const klass = t.filling_class || (t.name.match(/Klasa\s+(IV|V|III|II|I)/) || [])[1];
      const postEndo = t.post_endo || /me heqje nervi|RC/i.test(t.name || "");
      if (klass) latestFilling = { klass, postEndo };
    }
  });

  // Sipërfaqet e ruajtura direkt në rekordin e dhëmbit (pa procedurë) — shfaqen gjithmonë
  (record?.surfaces || []).forEach((s) => {
    if (!surfaceColors[s]) surfaceColors[s] = status === "decayed" ? "#9E3C3C" : CATEGORY_COLOR.default;
  });

  // Planned surfaces overlay
  const plannedSurfaces = {};
  if (showPlans) {
    (plans || []).filter((p) => p.status !== "perfunduar" && p.status !== "anuluar").forEach((p) => {
      (p.surfaces || []).forEach((s) => { plannedSurfaces[s] = CATEGORY_COLOR.default; });
    });
  }

  const isMissing = status === "missing";
  const { rootStatus, crownStatus } = deriveLayers(record);
  const hasKanal = rootStatus === "kanal" || rootStatus === "kanal_vide";
  const hasVide = rootStatus === "kanal_vide";
  const isImplant = rootStatus === "implant";
  const isCrown = crownStatus === "kurore";
  const isFasete = crownStatus === "fasete";
  const pontic = isMissing && !!bridgeInfo?.isPontic;
  const crownMat = record?.crown_material || "";
  const activeMat = bridgeInfo ? (bridgeInfo.material || crownMat) : (isCrown ? crownMat : "");

  // Photorealistic sprite per tooth type (public/teeth/*.png)
  const spriteKey = spriteKeyFor(number);
  const sprite = `/teeth/${spriteKey}.png`;
  const aspect = SPRITE_ASPECT[spriteKey];
  const drawnH = Math.min(H - 8, (W - 4) / aspect);
  const drawnW = drawnH * aspect;
  const drawnX = (W - drawnW) / 2;
  const drawnY = 6;
  const cervY = drawnY + drawnH * 0.42; // approx crown-root junction

  // Rrënjët të zgjatura: të gjithë dhëmbët arrijnë në të njëjtin nivel (kurora natyrale, rrënja shtrihet)
  const T = H - 8;
  const rootStretch = (drawnY + T - cervY) / (drawnY + drawnH - cervY);
  const stretchT = `translate(0 ${cervY}) scale(1 ${rootStretch}) translate(0 ${-cervY})`;
  const toothImage = (extra = {}) => (
    <g {...extra}>
      <image href={sprite} x={drawnX} y={drawnY} width={drawnW} height={drawnH} clipPath={`url(#crownclip-${number})`} />
      <g clipPath={`url(#rootclip-${number})`}>
        <g transform={stretchT}>
          <image href={sprite} x={drawnX} y={drawnY} width={drawnW} height={drawnH} />
        </g>
      </g>
    </g>
  );

  // Maxilla: roots UP · Mandibula: roots DOWN
  const transform = upper ? `translate(0,${H}) scale(1,-1)` : "";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`tooth-${number}`}
      title={`Dhëmbi ${number} · ${STATUS_OPTS.find(s => s.v === status)?.label || status}`}
      className={`group flex ${upper ? "flex-col" : "flex-col-reverse"} items-center gap-1 transition-transform hover:-translate-y-0.5 hover:drop-shadow-md`}
    >
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
        <defs>
          <pattern id={`hatch-${number}`} patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="4" stroke="#8A8A8A" strokeWidth="1.5" />
          </pattern>
          <filter id={`gray-${number}`}>
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <clipPath id={`crownclip-${number}`}>
            <rect x="0" y="0" width={W} height={cervY} />
          </clipPath>
          <clipPath id={`rootclip-${number}`}>
            <rect x="0" y={cervY - 0.5} width={W} height={drawnY + T - cervY + 0.5} />
          </clipPath>
          {activeMat && MAT_FX[activeMat] && (
            <filter id={`matfx-${number}`} colorInterpolationFilters="sRGB">
              <feColorMatrix type="saturate" values={`${MAT_FX[activeMat].sat}`} />
              <feComponentTransfer>
                <feFuncR type="linear" slope={MAT_FX[activeMat].slope} intercept={MAT_FX[activeMat].intercept} />
                <feFuncG type="linear" slope={MAT_FX[activeMat].slope} intercept={MAT_FX[activeMat].intercept} />
                <feFuncB type="linear" slope={MAT_FX[activeMat].slope} intercept={MAT_FX[activeMat].intercept} />
              </feComponentTransfer>
            </filter>
          )}
        </defs>

        <g transform={transform}>
          {pontic ? (
            <>
              <image href={sprite} x={drawnX} y={drawnY} width={drawnW} height={drawnH} clipPath={`url(#crownclip-${number})`} />
              {crownLayer(activeMat, sprite, number, drawnX, drawnY, drawnW, drawnH, cervY)}
            </>
          ) : isMissing ? (
            <>
              {toothImage({ opacity: "0.22", filter: `url(#gray-${number})` })}
              <line x1={drawnX + 4} y1={drawnY + 10} x2={drawnX + drawnW - 4} y2={drawnY + T - 10} stroke="#9E3C3C" strokeWidth="2.5" strokeLinecap="round" />
              <line x1={drawnX + drawnW - 4} y1={drawnY + 10} x2={drawnX + 4} y2={drawnY + T - 10} stroke="#9E3C3C" strokeWidth="2.5" strokeLinecap="round" />
            </>
          ) : (
            <>
              {/* Photorealistic tooth (implant: crown only + screw in root) */}
              {isImplant ? (
                <>
                  {/* Fikstura (implanti i klinikës) — platforma futet pas bazës së kurorës */}
                  {(() => {
                    const availH = drawnY + T - cervY + 4;
                    const iw = Math.min(availH * SPRITE_ASPECT["implant"], drawnW * 0.75);
                    const ih = iw / SPRITE_ASPECT["implant"];
                    return <image href="/teeth/implant.png" x={CX - iw / 2} y={cervY - 6} width={iw} height={ih} preserveAspectRatio="xMidYMid meet" />;
                  })()}
                  <image href={sprite} x={drawnX} y={drawnY} width={drawnW} height={drawnH} clipPath={`url(#crownclip-${number})`} />
                </>
              ) : (
                toothImage()
              )}

              {/* Root layer: anatomical red canal fills in EACH root + pulp chamber */}
              {!isImplant && hasKanal && (() => {
                const canals = ROOT_CANALS[spriteKey] || [];
                const px = (f) => drawnX + f[0] * drawnW;
                const py = (f) => drawnY + f[1] * drawnH;
                const ox = canals.map((c) => c.from[0]);
                const minX = Math.min(...ox), maxX = Math.max(...ox);
                const hw = 2.1;
                return (
                  <g data-testid={`canals-${number}`} transform={stretchT}>
                    <rect x={drawnX + minX * drawnW - 3} y={drawnY + drawnH * 0.4}
                          width={(maxX - minX) * drawnW + 6} height={drawnH * 0.055} rx="2.5"
                          fill="#C0392B" opacity="0.88" />
                    {canals.map((c, i) => (
                      <path key={i}
                            d={`M ${px(c.from) - hw} ${py(c.from)} Q ${px(c.bend) - hw * 0.6} ${py(c.bend)} ${px(c.to)} ${py(c.to)} Q ${px(c.bend) + hw * 0.6} ${py(c.bend)} ${px(c.from) + hw} ${py(c.from)} Z`}
                            fill="#C0392B" opacity="0.88" />
                    ))}
                  </g>
                );
              })()}
              {!isImplant && hasVide && (
                <g transform={stretchT}>
                  <rect x={CX - 2.5} y={cervY - drawnH * 0.1} width="5" height={drawnH * 0.3} rx="2"
                        fill="#98A2AE" stroke="#6B7280" strokeWidth="0.8" />
                </g>
              )}

              {/* Crown layer: gold outline / pink veneer */}
              {(isCrown || bridgeInfo) && crownLayer(activeMat, sprite, number, drawnX, drawnY, drawnW, drawnH, cervY)}
              {isFasete && (
                <path d={`M ${drawnX + drawnW * 0.24} ${drawnY + 8} Q ${drawnX + drawnW * 0.08} ${drawnY + drawnH * 0.21} ${drawnX + drawnW * 0.24} ${cervY - 6}`}
                      stroke="#EC4899" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.9" />
              )}

              {/* Surfaces (overlay on crown) */}
              {surfaces.map((s) => {
                const treated = surfaceColors[s];
                const planned = plannedSurfaces[s];
                if (treated) {
                  return <path key={s} d={surfacePath(s, anterior)} fill={treated} stroke="#FFFFFF" strokeWidth="0.5" opacity="0.9" />;
                }
                if (planned) {
                  return <path key={s} d={surfacePath(s, anterior)} fill={`url(#hatch-${number})`} stroke="#3B6FA8" strokeWidth="0.8" strokeDasharray="2 1" />;
                }
                return null;
              })}

              {/* Decayed indicator: red blot */}
              {status === "decayed" && !Object.keys(surfaceColors).length && (
                <circle cx={CX} cy="28" r="3.5" fill="#9E3C3C" opacity="0.85" />
              )}
            </>
          )}

          {bridgeInfo && !(isMissing && !pontic) && (() => {
            const barY = drawnY + drawnH * 0.13;
            const cs = CONNECTOR_FX[bridgeInfo.material] || CONNECTOR_FX[""];
            return (
              <g data-testid={`bridge-bar-${number}`}>
                {bridgeInfo.hasLeft && (
                  <g>
                    <rect x={-16} y={barY} width={CX + 16} height={9} rx={3.5} fill={cs.fill} stroke={cs.stroke} strokeWidth="1" />
                    <rect x={-15} y={barY + 6.2} width={CX + 14} height={2.2} rx={1.1} fill="rgba(70,80,90,0.16)" />
                  </g>
                )}
                {bridgeInfo.hasRight && (
                  <g>
                    <rect x={CX} y={barY} width={W - CX + 16} height={9} rx={3.5} fill={cs.fill} stroke={cs.stroke} strokeWidth="1" />
                    <rect x={CX + 1} y={barY + 6.2} width={W - CX + 14} height={2.2} rx={1.1} fill="rgba(70,80,90,0.16)" />
                  </g>
                )}
              </g>
            );
          })()}
        </g>

        {bridgeSelected && (
          <rect x="1" y="1" width={W - 2} height={H - 2} rx="8" fill="none" stroke="#0E7490" strokeWidth="2.5" strokeDasharray="5 3" />
        )}
      </svg>

      {/* Filling class badge */}
      {latestFilling && (
        <span
          data-testid={`tooth-class-${number}`}
          title={`Mbushje Klasa ${latestFilling.klass}${latestFilling.postEndo ? " (me heqje nervi)" : ""}`}
          className={`mt-0.5 px-1 py-px text-[8px] font-bold rounded ${
            latestFilling.postEndo ? "bg-[#C04848] text-white" : "bg-[#3B6FA8] text-white"
          }`}
        >
          {latestFilling.klass}{latestFilling.postEndo ? "·N" : ""}
        </span>
      )}
      {(() => {
        const mat = activeMat;
        if (!mat) return null;
        const info = CROWN_MATERIALS.find((m) => m.v === mat);
        return (
          <span data-testid={`tooth-mat-${number}`} title={info?.label}
                className="px-1 py-px text-[8px] font-bold rounded text-white" style={{ background: info?.badge }}>
            {info?.short}
          </span>
        );
      })()}
      <span className="text-[10px] font-mono text-[#5C5C5C] group-hover:text-[#2C4C3B]">{number}</span>
    </button>
  );
}

const ALL_SURFACES = ["M", "O", "I", "D", "B", "L", "C"];
const SURFACE_LABEL = { M: "Mezial", O: "Okluzal", I: "Incizival", D: "Distal", B: "Bukal", L: "Lingual", C: "Cervikale" };

export default function Odontogram({ patientId }) {
  const { format, symbol } = useCurrency();
  const [records, setRecords] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [showPlans, setShowPlans] = useState(true);
  const [mode, setMode] = useState("2d");
  const [selected, setSelected] = useState(null);

  // editor state
  const [status, setStatus] = useState("healthy");
  const [rootStatus, setRootStatus] = useState("normal");
  const [crownStatus, setCrownStatus] = useState("natyrale");
  const [surfaces, setSurfaces] = useState([]);
  const [procedureId, setProcedureId] = useState("");
  const [cost, setCost] = useState(0);
  const [notes, setNotes] = useState("");
  const [intent, setIntent] = useState("done");   // "done" = trajtim i kryer | "plan" = plan i ardhshëm
  const [klass, setKlass] = useState("");          // I, II, III, IV, V
  const [postEndo, setPostEndo] = useState(false); // me/pa heqje nervi
  const [saving, setSaving] = useState(false);
  const [crownMaterial, setCrownMaterial] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);

  // bridge state
  const [bridges, setBridges] = useState([]);
  const [bridgeMode, setBridgeMode] = useState(false);
  const [bridgeTeeth, setBridgeTeeth] = useState([]);
  const [bridgePontics, setBridgePontics] = useState([]);
  const [bridgeMaterial, setBridgeMaterial] = useState("");
  const [bridgeSaving, setBridgeSaving] = useState(false);

  const recordMap = useMemo(() => {
    const m = {}; records.forEach((r) => { m[r.tooth_number] = r; }); return m;
  }, [records]);

  const treatmentsByTooth = useMemo(() => {
    const m = {};
    treatments.forEach((t) => {
      if (!t.tooth_number) return;
      (m[t.tooth_number] ||= []).push(t);
    });
    return m;
  }, [treatments]);

  const plansByTooth = useMemo(() => {
    const m = {};
    plans.forEach((p) => {
      if (!p.tooth_number) return;
      (m[p.tooth_number] ||= []).push(p);
    });
    return m;
  }, [plans]);

  const bridgeByTooth = useMemo(() => {
    const m = {};
    bridges.forEach((b) => {
      const sorted = (b.teeth || []).slice().sort((a, c) => visualIndex(a) - visualIndex(c));
      sorted.forEach((t, i) => {
        m[t] = {
          bridgeId: b.id,
          material: b.material || "",
          isPontic: (b.pontic_teeth || []).includes(t),
          hasLeft: i > 0,
          hasRight: i < sorted.length - 1,
        };
      });
    });
    return m;
  }, [bridges]);

  const load = useCallback(async () => {
    const [r, t, pl, pr, br] = await Promise.all([
      api.get(`/odontogram/${patientId}`),
      api.get(`/treatments`, { params: { patient_id: patientId } }),
      api.get(`/treatment-plans`, { params: { patient_id: patientId } }),
      api.get(`/procedures`),
      api.get(`/odontogram/${patientId}/bridges`),
    ]);
    setRecords(r.data);
    setTreatments(t.data);
    setPlans(pl.data);
    setProcedures(pr.data);
    setBridges(br.data);
  }, [patientId]);
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 0);
    return () => clearTimeout(t);
  }, [load]);

  const openTooth = (num) => {
    setSelected(num);
    const rec = recordMap[num];
    const legacy = rec?.status || "healthy";
    setStatus(["crown", "root_canal", "implant"].includes(legacy) ? "treated" : legacy);
    const layers = deriveLayers(rec);
    setRootStatus(layers.rootStatus);
    setCrownStatus(layers.crownStatus);
    setCrownMaterial(rec?.crown_material || "");
    setSurfaces([]);
    setNotes("");
    setProcedureId("");
    setCost(0);
    setIntent("done");
    setKlass("");
    setPostEndo(false);
  };

  const toggleSurface = (s) => {
    setSurfaces((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);
  };

  const toggleBridgeTooth = (n) => {
    setBridgeTeeth((p) => {
      if (p.includes(n)) {
        setBridgePontics((q) => q.filter((x) => x !== n));
        return p.filter((x) => x !== n);
      }
      if (recordMap[n]?.status === "missing") setBridgePontics((q) => [...q, n]);
      return [...p, n];
    });
  };
  const togglePontic = (n) => setBridgePontics((p) => p.includes(n) ? p.filter((x) => x !== n) : [...p, n]);
  const exitBridgeMode = () => { setBridgeMode(false); setBridgeTeeth([]); setBridgePontics([]); setBridgeMaterial(""); };

  const saveBridge = async () => {
    if (bridgeTeeth.length < 2) { toast.error("Zgjidhni të paktën 2 dhëmbë"); return; }
    const arches = new Set(bridgeTeeth.map((t) => (t[0] === "1" || t[0] === "2" ? "u" : "l")));
    if (arches.size > 1) { toast.error("Ura duhet të jetë në të njëjtën nofull"); return; }
    setBridgeSaving(true);
    try {
      await api.post(`/odontogram/${patientId}/bridges`, {
        teeth: bridgeTeeth.slice().sort((a, b) => visualIndex(a) - visualIndex(b)),
        pontic_teeth: bridgePontics,
        material: bridgeMaterial,
      });
      toast.success("Ura u ruajt");
      exitBridgeMode();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gabim gjatë ruajtjes së urës");
    } finally {
      setBridgeSaving(false);
    }
  };

  const deleteBridge = async (id) => {
    try {
      await api.delete(`/odontogram/${patientId}/bridges/${id}`);
      toast.success("Ura u fshi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gabim gjatë fshirjes");
    }
  };

  const proc = procedures.find((p) => p.id === procedureId);
  const allowedSurfaces = selected && isAnterior(selected) ? ANTERIOR_SURFACES : POSTERIOR_SURFACES;
  const isMbushje = proc?.category === "Mbushje";

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // 1) update tooth record (status + surfaces) always
      await api.post(`/odontogram/${patientId}`, {
        tooth_number: selected,
        status,
        surfaces,
        procedure_name: proc?.name || "",
        notes,
        root_status: rootStatus,
        crown_status: crownStatus,
        crown_material: crownStatus === "kurore" ? crownMaterial : "",
      });

      // 2) Create either a completed Treatment OR a TreatmentPlan
      if (proc) {
        const payload = {
          patient_id: patientId,
          tooth_number: selected,
          surfaces,
          category: proc.category || "",
          ...(isMbushje && klass ? { filling_class: klass, post_endo: postEndo } : {}),
        };
        if (intent === "plan") {
          await api.post(`/treatment-plans`, {
            ...payload,
            procedure_name: proc.name,
            procedure_id: proc.id,
            estimated_cost: Number(cost) || proc.default_cost || 0,
            priority: "e_mesme",
            status: "planifikuar",
            notes: notes || "",
          });
          toast.success(`Plan i shtuar për Dh. ${selected}`);
        } else {
          await api.post(`/treatments`, {
            ...payload,
            name: proc.name,
            description: notes || "",
            status: "perfunduar",
            cost: Number(cost) || proc.default_cost || 0,
          });
          toast.success(`Trajtim i kryer për Dh. ${selected}`);
        }
      } else {
        toast.success(`Dhëmbi ${selected} u përditësua`);
      }
      setSelected(null);
      load();
    } catch {
      toast.error("Gabim gjatë ruajtjes");
    } finally {
      setSaving(false);
    }
  };

  // Per-tooth history for the selected tooth
  const selectedTreatments = selected ? (treatmentsByTooth[selected] || []) : [];
  const selectedPlans = selected ? (plansByTooth[selected] || []) : [];

  const renderTooth = (n) => (
    <Tooth key={n} number={n} record={recordMap[n]} treatments={treatmentsByTooth[n]} plans={plansByTooth[n]}
           showPlans={showPlans} bridgeInfo={bridgeByTooth[n]} bridgeSelected={bridgeMode && bridgeTeeth.includes(n)}
           onClick={() => (bridgeMode ? toggleBridgeTooth(n) : openTooth(n))} />
  );

  return (
    <div className="surface-card p-6 md:p-8" data-testid="odontogram">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow">Odontograma</div>
          <h3 className="mt-1 text-2xl font-semibold tracking-tight">Karta Dentare (FDI)</h3>
          <p className="mt-1 text-sm text-[#5C5C5C]">Dhëmbë realistë — klikoni një dhëmb për histori dhe ndërhyrje të reja.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-[#E2DFD8] overflow-hidden" data-testid="odontogram-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("2d")}
              data-testid="mode-2d"
              className={`px-4 py-1.5 text-sm font-semibold transition-colors ${mode === "2d" ? "bg-[#2C4C3B] text-[#FDFBF7]" : "text-[#5C5C5C] hover:bg-[#EAE4D9]"}`}
            >
              2D
            </button>
            <button
              type="button"
              onClick={() => setMode("3d")}
              data-testid="mode-3d"
              className={`px-4 py-1.5 text-sm font-semibold transition-colors ${mode === "3d" ? "bg-[#2C4C3B] text-[#FDFBF7]" : "text-[#5C5C5C] hover:bg-[#EAE4D9]"}`}
            >
              3D
            </button>
          </div>
          <button
            type="button"
            onClick={() => (bridgeMode ? exitBridgeMode() : setBridgeMode(true))}
            data-testid="bridge-mode-toggle"
            className={`flex items-center gap-2 rounded-lg border px-4 py-1.5 text-sm font-semibold transition-colors ${
              bridgeMode ? "border-[#0E7490] bg-[#0E7490] text-white" : "border-[#E2DFD8] text-[#5C5C5C] hover:bg-[#EAE4D9]"
            }`}
          >
            <Link2 size={14} /> Urë Dentare
          </button>
          <button
            onClick={() => setShowPlans((s) => !s)}
            className="btn-ghost flex items-center gap-2"
            data-testid="toggle-plans"
          >
            {showPlans ? <Eye size={14} /> : <EyeOff size={14} />}
            {showPlans ? "Fshih Planet" : "Shfaq Planet"}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6 rounded-lg border border-[#E2DFD8] bg-[#FDFBF7] p-4">
        <div className="label-eyebrow mb-3">Legjenda</div>
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
          {Object.entries(CATEGORY_COLOR).filter(([k]) => k !== "default").map(([cat, color]) => (
            <div key={cat} className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded" style={{ background: color }} />
              <span className="text-[#5C5C5C]">{cat}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <svg width="14" height="14"><line x1="2" y1="2" x2="2" y2="12" stroke="#C04848" strokeWidth="2" /></svg>
            <span className="text-[#5C5C5C]">Kanal (RC)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded border-2 border-[#C9963A]"></span>
            <span className="text-[#5C5C5C]">Kurorë</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#9E3C3C] font-bold text-base leading-none">⨯</span>
            <span className="text-[#5C5C5C]">Mungon</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded bg-[#5A5A5A]"></span>
            <span className="text-[#5C5C5C]">Implant</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3.5 w-1.5 rounded-sm bg-[#98A2AE] border border-[#6B7280]"></span>
            <span className="text-[#5C5C5C]">Vidë (post)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded border-2 border-[#EC4899]"></span>
            <span className="text-[#5C5C5C]">Fasetë</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-1.5 w-5 rounded-full bg-[#C9963A] border border-[#8B8F94]"></span>
            <span className="text-[#5C5C5C]">Urë dentare</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="12" height="12"><rect width="12" height="12" fill="url(#hatchLegend)" /><defs><pattern id="hatchLegend" patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="4" stroke="#3B6FA8" strokeWidth="1.5" /></pattern></defs></svg>
            <span className="text-[#5C5C5C]">I planifikuar</span>
          </div>
        </div>
      </div>

      {bridgeMode && (
        <div className="mb-6 rounded-lg border-2 border-[#0E7490]/50 bg-[#0E7490]/5 p-4" data-testid="bridge-panel">
          <div className="flex items-center gap-2 mb-1">
            <Link2 size={14} className="text-[#0E7490]" />
            <div className="label-eyebrow">Urë e Re Dentare</div>
          </div>
          <p className="text-xs text-[#5C5C5C] mb-3">
            Klikoni dhëmbët e urës në kartë (mbajtësit + ata që zëvendësohen). Pastaj klikoni etiketën e çdo dhëmbi për të ndërruar rolin Mbajtës ↔ Pontik.
          </p>
          {bridgeTeeth.length === 0 ? (
            <div className="text-sm italic text-[#5C5C5C] mb-3" data-testid="bridge-empty-hint">Asnjë dhëmb i zgjedhur ende.</div>
          ) : (
            <div className="flex flex-wrap gap-2 mb-3">
              {bridgeTeeth.slice().sort((a, b) => visualIndex(a) - visualIndex(b)).map((n) => {
                const isP = bridgePontics.includes(n);
                return (
                  <button key={n} type="button" onClick={() => togglePontic(n)} data-testid={`bridge-chip-${n}`}
                          className={`rounded-full border-2 px-3 py-1 text-xs font-semibold transition-colors ${
                            isP ? "border-[#C9963A] bg-[#C9963A] text-white" : "border-[#0E7490] bg-white text-[#0E7490]"
                          }`}>
                    Dh. {n} · {isP ? "Pontik" : "Mbajtës"}
                  </button>
                );
              })}
            </div>
          )}
          <div className="mb-3">
            <div className="label-eyebrow mb-2">Materiali i Urës</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setBridgeMaterial("")} data-testid="bridge-mat-none"
                      className={`rounded-lg border-2 px-3 py-1.5 text-xs font-medium transition-colors ${
                        bridgeMaterial === "" ? "border-[#2C4C3B] bg-[#2C4C3B] text-white" : "border-[#E2DFD8] text-[#5C5C5C]"
                      }`}>
                Pa përcaktuar
              </button>
              {CROWN_MATERIALS.map((m) => (
                <button key={m.v} type="button" onClick={() => setBridgeMaterial(m.v)} data-testid={`bridge-mat-${m.v}`}
                        className={`rounded-lg border-2 px-3 py-1.5 text-xs font-medium transition-colors ${
                          bridgeMaterial === m.v ? "text-white" : "border-[#E2DFD8] text-[#5C5C5C]"
                        }`}
                        style={bridgeMaterial === m.v ? { background: m.badge, borderColor: m.badge } : {}}>
                  {m.label}
                </button>
              ))}
              <button type="button" onClick={() => setCompareOpen(true)} data-testid="bridge-open-compare"
                      className="flex items-center gap-1 rounded-lg border border-[#7C3AED]/40 px-3 py-1.5 text-xs font-semibold text-[#7C3AED] hover:bg-[#7C3AED]/10">
                <Sparkles size={12} /> Si do duken? Krahaso
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={exitBridgeMode} data-testid="bridge-cancel">Anulo</button>
            <button className="btn-primary" onClick={saveBridge} disabled={bridgeSaving || bridgeTeeth.length < 2} data-testid="bridge-save">
              {bridgeSaving ? "Duke ruajtur…" : `Ruaj Urën (${bridgeTeeth.length} dhëmbë)`}
            </button>
          </div>
        </div>
      )}

      {mode === "3d" ? (
        <Suspense fallback={<div className="flex items-center justify-center h-[560px] rounded-xl bg-[#0F1728] text-sm text-white/60" data-testid="odontogram-3d-loading">Duke ngarkuar pamjen 3D…</div>}>
          <Odontogram3D
            recordMap={recordMap}
            treatmentsByTooth={treatmentsByTooth}
            plansByTooth={plansByTooth}
            showPlans={showPlans}
            bridges={bridges}
            bridgeByTooth={bridgeByTooth}
            onTogglePlans={() => setShowPlans((s) => !s)}
            onSelectTooth={(n) => (bridgeMode ? toggleBridgeTooth(n) : openTooth(n))}
            selectedTooth={selected}
          />
        </Suspense>
      ) : (
      <div className="space-y-8 overflow-x-auto">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5C5C] mb-3">Maxilla (Lart)</div>
          <div className="flex justify-center gap-2 min-w-max">
            <div className="flex gap-2 pr-4 border-r border-[#E2DFD8]">
              {UPPER_RIGHT.map(renderTooth)}
            </div>
            <div className="flex gap-2 pl-4">
              {UPPER_LEFT.map(renderTooth)}
            </div>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5C5C] mb-3">Mandibula (Poshtë)</div>
          <div className="flex justify-center gap-2 min-w-max">
            <div className="flex gap-2 pr-4 border-r border-[#E2DFD8]">
              {LOWER_RIGHT.slice().reverse().map(renderTooth)}
            </div>
            <div className="flex gap-2 pl-4">
              {LOWER_LEFT.slice().reverse().map(renderTooth)}
            </div>
          </div>
        </div>
      </div>
      )}

      {bridges.length > 0 && (
        <div className="mt-6 rounded-lg border border-[#E2DFD8] bg-[#FDFBF7] p-4" data-testid="bridge-list">
          <div className="label-eyebrow mb-3">Urat Dentare ({bridges.length})</div>
          <ul className="space-y-2">
            {bridges.map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-3 rounded border border-[#E2DFD8] bg-white px-3 py-2 text-sm" data-testid={`bridge-row-${b.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Link2 size={13} className="text-[#0E7490]" />
                  <span className="font-semibold font-mono">{(b.teeth || []).join("–")}</span>
                  {b.material && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                          style={{ background: CROWN_MATERIALS.find((m) => m.v === b.material)?.badge || "#6B7280" }}>
                      {MATERIAL_LABEL[b.material]}
                    </span>
                  )}
                  {(b.pontic_teeth || []).length > 0 && (
                    <span className="text-xs text-[#5C5C5C]">Pontik: {b.pontic_teeth.join(", ")}</span>
                  )}
                </div>
                <button onClick={() => deleteBridge(b.id)} className="rounded p-1 text-[#9E3C3C] hover:bg-[#9E3C3C]/10"
                        data-testid={`bridge-delete-${b.id}`} title="Fshi urën">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Dhëmbi {selected}
              <span className="text-xs font-normal text-[#5C5C5C]">
                {selected && isAnterior(selected) ? "(anterior)" : "(posterior)"}
              </span>
              {selected && recordMap[selected] && (
                <span className="ml-2 rounded-full bg-[#EAE4D9] px-2 py-0.5 text-[10px] font-medium text-[#2C4C3B] uppercase">
                  {STATUS_OPTS.find((s) => s.v === recordMap[selected].status)?.label || recordMap[selected].status}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* ─── URA — nëse dhëmbi është pjesë e një ure, trego + mundëso fshirjen ─── */}
          {selected && bridgeByTooth[selected] && (() => {
            const b = bridges.find((x) => x.id === bridgeByTooth[selected].bridgeId);
            if (!b) return null;
            return (
              <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-[#0E7490]/40 bg-[#0E7490]/5 px-3 py-2" data-testid="tooth-bridge-banner">
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <Link2 size={13} className="text-[#0E7490]" />
                  <span className="font-medium">Pjesë e urës</span>
                  <span className="font-mono font-semibold">{(b.teeth || []).join("–")}</span>
                  {b.material && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                          style={{ background: CROWN_MATERIALS.find((m) => m.v === b.material)?.badge || "#6B7280" }}>
                      {MATERIAL_LABEL[b.material]}
                    </span>
                  )}
                  {(b.pontic_teeth || []).includes(selected) && (
                    <span className="rounded-full bg-[#C9963A] px-2 py-0.5 text-[10px] font-bold text-white">Pontik</span>
                  )}
                </div>
                <button
                  onClick={() => { deleteBridge(b.id); setSelected(null); }}
                  className="flex items-center gap-1 shrink-0 rounded-md border border-[#9E3C3C]/40 px-2.5 py-1.5 text-xs font-semibold text-[#9E3C3C] hover:bg-[#9E3C3C]/10 transition-colors"
                  data-testid="tooth-bridge-delete"
                >
                  <Trash2 size={12} /> Fshi Urën
                </button>
              </div>
            );
          })()}

          {/* ─── HISTORIKU I DHËMBIT (në krye) ─── */}
          <section className="mt-2 rounded-lg border border-[#E2DFD8] bg-[#FDFBF7] p-4" data-testid="tooth-history-section">
            <div className="flex items-center gap-2 mb-3">
              <History size={14} className="text-[#2C4C3B]" />
              <div className="label-eyebrow">Historiku i Dhëmbit</div>
              <span className="ml-auto text-xs text-[#5C5C5C]">
                {selectedTreatments.length} trajtime · {selectedPlans.length} plane
              </span>
            </div>

            {selectedTreatments.length === 0 && selectedPlans.length === 0 ? (
              <div className="text-sm text-[#5C5C5C] italic">Pa histori për këtë dhëmb — ky është trajtimi i parë.</div>
            ) : (
              <div className="space-y-3">
                {selectedTreatments.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#2C4C3B] mb-1.5 flex items-center gap-1">
                      <CheckCircle2 size={10} /> Të Kryera
                    </div>
                    <ul className="divide-y divide-[#E2DFD8] rounded border border-[#E2DFD8] bg-white">
                      {selectedTreatments
                        .slice()
                        .sort((a, b) => (b.performed_at || "").localeCompare(a.performed_at || ""))
                        .map((t) => {
                          const cat = t.category || categorizeByName(t.name);
                          const color = CATEGORY_COLOR[cat] || CATEGORY_COLOR.default;
                          return (
                            <li key={t.id} className="px-3 py-2 text-sm" data-testid={`tooth-hist-tx-${t.id}`}>
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2 font-medium min-w-0">
                                  <span className="inline-block h-3 w-3 rounded-sm shrink-0" style={{ background: color }} />
                                  <span className="truncate">{t.name}</span>
                                  {t.surfaces?.length > 0 && (
                                    <span className="text-xs text-[#5C5C5C] font-mono">[{t.surfaces.join("·")}]</span>
                                  )}
                                  {t.filling_class && (
                                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white ${t.post_endo ? "bg-[#C04848]" : "bg-[#3B6FA8]"}`}>
                                      Kl. {t.filling_class}{t.post_endo ? "·N" : ""}
                                    </span>
                                  )}
                                </div>
                                <div className="font-mono text-xs text-[#2C4C3B]">{format(t.cost || 0)}</div>
                              </div>
                              <div className="text-xs text-[#5C5C5C] mt-0.5">
                                {t.performed_at ? new Date(t.performed_at).toLocaleDateString("sq-AL", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                                {t.performed_by && <> · {t.performed_by}</>}
                                {t.description && <> · {t.description}</>}
                              </div>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}

                {selectedPlans.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#8B6914] mb-1.5 flex items-center gap-1">
                      <ClipboardList size={10} /> Të Planifikuara
                    </div>
                    <ul className="divide-y divide-[#E2DFD8] rounded border border-[#E2DFD8] bg-white">
                      {selectedPlans.map((p) => (
                        <li key={p.id} className="px-3 py-2 text-sm" data-testid={`tooth-hist-plan-${p.id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 font-medium min-w-0">
                              <span className="truncate">{p.procedure_name}</span>
                              {p.surfaces?.length > 0 && (
                                <span className="text-xs text-[#5C5C5C] font-mono">[{p.surfaces.join("·")}]</span>
                              )}
                              {p.filling_class && (
                                <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white ${p.post_endo ? "bg-[#C04848]" : "bg-[#3B6FA8]"}`}>
                                  Kl. {p.filling_class}{p.post_endo ? "·N" : ""}
                                </span>
                              )}
                              <span className="rounded-full bg-[#D4A24C]/20 px-2 py-0.5 text-[10px] font-medium text-[#8B6914]">
                                {p.status}
                              </span>
                            </div>
                            <div className="font-mono text-xs text-[#8B6914]">{format(p.estimated_cost || 0)}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ─── SHTO TRAJTIM TË RI / PLAN ─── */}
          <section className="mt-5 space-y-4" data-testid="tooth-new-tx-section">
            <div className="flex items-center gap-2">
              <Plus size={14} className="text-[#2C4C3B]" />
              <div className="label-eyebrow">Shto Trajtimin e Radhës</div>
            </div>

            {/* Intent toggle: Trajtim i kryer vs Plan i ardhshëm */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setIntent("done")}
                data-testid="intent-done"
                className={`rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  intent === "done"
                    ? "border-[#2C4C3B] bg-[#2C4C3B] text-[#FDFBF7]"
                    : "border-[#E2DFD8] text-[#5C5C5C] hover:border-[#2C4C3B]"
                }`}
              >
                <CheckCircle2 size={14} /> Trajtim i Kryer (sot)
              </button>
              <button
                type="button"
                onClick={() => setIntent("plan")}
                data-testid="intent-plan"
                className={`rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  intent === "plan"
                    ? "border-[#C9963A] bg-[#C9963A] text-white"
                    : "border-[#E2DFD8] text-[#5C5C5C] hover:border-[#C9963A]"
                }`}
              >
                <CalendarClock size={14} /> Plan për më Vonë
              </button>
            </div>

            <div>
              <Label className="label-eyebrow">Gjendja e Dhëmbit</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-2" data-testid="tooth-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BASE_STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Shtresat: Rrënja + Kurora (kombinohen lirshëm) */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="label-eyebrow">Rrënja</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {ROOT_OPTS.map((o) => (
                    <button key={o.v} type="button" onClick={() => setRootStatus(o.v)} data-testid={`root-${o.v}`}
                            className={`rounded-lg border-2 px-2 py-2 text-xs font-medium transition-colors ${
                              rootStatus === o.v
                                ? "border-[#C04848] bg-[#C04848] text-white"
                                : "border-[#E2DFD8] text-[#5C5C5C] hover:border-[#C04848]"
                            }`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Kurora</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {CROWN_OPTS.map((o) => (
                    <button key={o.v} type="button" onClick={() => setCrownStatus(o.v)} data-testid={`crown-${o.v}`}
                            className={`rounded-lg border-2 px-2 py-2 text-xs font-medium transition-colors ${
                              crownStatus === o.v
                                ? "border-[#C9963A] bg-[#C9963A] text-white"
                                : "border-[#E2DFD8] text-[#5C5C5C] hover:border-[#C9963A]"
                            }`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                {crownStatus !== "natyrale" && rootStatus === "normal" && (
                  <p className="mt-1.5 text-[11px] text-[#8B6914]">Kujtesë: nëse dhëmbi ka kanal/vidë, zgjidheni edhe te "Rrënja".</p>
                )}
                {crownStatus === "kurore" && (
                  <div className="mt-3" data-testid="crown-material-section">
                    <Label className="label-eyebrow">Materiali</Label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {CROWN_MATERIALS.map((m) => (
                        <button key={m.v} type="button" data-testid={`crown-mat-${m.v}`}
                                onClick={() => setCrownMaterial(crownMaterial === m.v ? "" : m.v)}
                                className={`rounded-lg border-2 px-1 py-1.5 text-[11px] font-medium transition-colors ${
                                  crownMaterial === m.v ? "text-white" : "border-[#E2DFD8] text-[#5C5C5C]"
                                }`}
                                style={crownMaterial === m.v ? { background: m.badge, borderColor: m.badge } : {}}>
                          {m.label}
                        </button>
                      ))}
                    </div>
                    <button type="button" onClick={() => setCompareOpen(true)} data-testid="open-material-compare"
                            className="mt-2 flex items-center gap-1 text-xs font-semibold text-[#7C3AED] hover:underline">
                      <Sparkles size={12} /> Si do duken dhëmbët? Krahaso materialet
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="label-eyebrow">Sipërfaqet e Prekura</Label>
              <div className="mt-2 grid grid-cols-6 gap-2">
                {ALL_SURFACES.filter((s) => allowedSurfaces.includes(s)).map((s) => (
                  <button key={s} type="button" onClick={() => toggleSurface(s)} data-testid={`surface-${s}`}
                          className={`rounded-lg border-2 py-2 text-sm font-medium transition-colors ${
                            surfaces.includes(s)
                              ? "border-[#2C4C3B] bg-[#2C4C3B] text-[#FDFBF7]"
                              : "border-[#E2DFD8] text-[#5C5C5C] hover:border-[#2C4C3B]"
                          }`}>
                    <div className="text-xs font-bold">{s}</div>
                    <div className="text-[10px] opacity-80">{SURFACE_LABEL[s]}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="label-eyebrow">Procedura</Label>
              <Select value={procedureId} onValueChange={(v) => {
                setProcedureId(v);
                const found = procedures.find((p) => p.id === v);
                if (found) setCost(found.default_cost);
              }}>
                <SelectTrigger className="mt-2" data-testid="tooth-procedure-select">
                  <SelectValue placeholder="Zgjidh procedurë" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {procedures.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="inline-block w-2.5 h-2.5 rounded-sm mr-2 align-middle" style={{ background: CATEGORY_COLOR[p.category] || CATEGORY_COLOR.default }}></span>
                      {p.name} · {format(p.default_cost)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {proc && (
                <div className="mt-2 text-xs text-[#5C5C5C]">
                  Kategoria: <span className="font-medium" style={{ color: CATEGORY_COLOR[proc.category] || CATEGORY_COLOR.default }}>{proc.category}</span>
                </div>
              )}
            </div>

            {/* Filling class + Root canal (vetëm për Mbushje) */}
            {isMbushje && (
              <div className="rounded-lg border border-[#3B6FA8]/30 bg-[#3B6FA8]/5 p-3 space-y-3">
                <div>
                  <Label className="label-eyebrow">Klasifikimi Black (Klasa)</Label>
                  <div className="mt-2 grid grid-cols-6 gap-2">
                    <button type="button" onClick={() => setKlass("")}
                            data-testid="klass-none"
                            className={`rounded-md border-2 py-1.5 text-xs font-bold transition-colors ${
                              klass === "" ? "border-[#2C4C3B] bg-[#2C4C3B] text-[#FDFBF7]" : "border-[#E2DFD8] text-[#5C5C5C]"
                            }`}>—</button>
                    {["I","II","III","IV","V"].map((k) => (
                      <button key={k} type="button" onClick={() => setKlass(k)}
                              data-testid={`klass-${k}`}
                              className={`rounded-md border-2 py-1.5 text-xs font-bold transition-colors ${
                                klass === k ? "border-[#3B6FA8] bg-[#3B6FA8] text-white" : "border-[#E2DFD8] text-[#5C5C5C] hover:border-[#3B6FA8]"
                              }`}>{k}</button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="post-endo-toggle">
                  <input type="checkbox" checked={postEndo} onChange={(e) => setPostEndo(e.target.checked)}
                         className="h-4 w-4 rounded border-[#E2DFD8] text-[#C04848] focus:ring-[#C04848]" />
                  <span className="text-sm">Me heqje nervi (pas trajtimit të kanalit / RC)</span>
                </label>
              </div>
            )}

            {procedureId && (
              <div>
                <Label className="label-eyebrow">{intent === "plan" ? `Vlerësim Kosto (${symbol})` : `Kosto (${symbol})`}</Label>
                <input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)}
                       data-testid="tooth-cost-input"
                       className="mt-2 w-full rounded-md border border-[#E2DFD8] px-3 py-2 text-sm" />
              </div>
            )}

            <div>
              <Label className="label-eyebrow">Shënime</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2" data-testid="tooth-notes-input" />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#E2DFD8]">
              <button className="btn-ghost" onClick={() => setSelected(null)}>Anulo</button>
              <button className="btn-primary" onClick={save} disabled={saving} data-testid="tooth-save-button">
                {saving ? "Duke ruajtur…" : (intent === "plan" ? "Ruaj Planin" : "Ruaj Trajtimin")}
              </button>
            </div>
          </section>
        </DialogContent>
      </Dialog>

      <CrownMaterialCompare
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        onPick={(v) => {
          if (bridgeMode) setBridgeMaterial(v);
          else { setCrownMaterial(v); setCrownStatus("kurore"); }
          setCompareOpen(false);
          toast.success(`Materiali u zgjodh: ${MATERIAL_LABEL[v]}`);
        }}
      />
    </div>
  );
}
